import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { compact } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, Select, Tooltip, useStateInRef } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { makeInitialOneTimeInStages } from '~/business-logic/billingLogic';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { serviceBillingModel } from '~/models';
import { RecurrencePeriodTypes } from '~/models/service-billing';
import { PreapprovalPeriodType } from '~/models/service-billing/model';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { getOccurrenceDisabledData } from '../ServiceBuilder.utils';
const getOccurrenceOptions = (showOnDemand)=>compact([
        showOnDemand ? {
            value: 'onDemand'
        } : null,
        {
            value: 'onetime'
        },
        {
            value: 'inStages'
        },
        {
            value: RecurrencePeriodTypes.Weekly
        },
        {
            value: RecurrencePeriodTypes.BiWeekly
        },
        {
            value: RecurrencePeriodTypes.Monthly
        },
        {
            value: RecurrencePeriodTypes.Quarterly
        },
        {
            value: RecurrencePeriodTypes.Yearly
        }
    ]);
export const deprecatedOccurrenceTextFieldTestIds = {
    OccurrenceTextFieldTestIds: {
        Select: 'sb-occurrence',
        Option: (value)=>"sb-occurrence-opt-".concat(value)
    }
};
export const GetOccurrenceTextFieldTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: deprecatedOccurrenceTextFieldTestIds
    });
export default function OccurrenceTextField(props) {
    const { serviceOrigin, service } = props;
    const { OccurrenceTextFieldTestIds } = useMemo(()=>GetOccurrenceTextFieldTID(), []);
    const getProps = useStateInRef(props);
    const { control, watch, trigger, setValue } = useFormContext();
    const { disabled, tooltip } = getOccurrenceDisabledData(serviceOrigin, service);
    const { YEARLY_AUTOMATIC_SERVICE, ON_DEMAND } = useFeatureFlags();
    const billingTrigger = watch('trigger');
    const showOnDemand = !!ON_DEMAND;
    useEffect(()=>{
        trigger('recurrencePeriod');
    }, [
        billingTrigger,
        trigger
    ]);
    return /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "recurrencePeriod",
        rules: {
            validate: (value)=>{
                if (billingTrigger === 'ongoing' && value === RecurrencePeriodTypes.Yearly && !YEARLY_AUTOMATIC_SERVICE) {
                    return "Invalid value";
                }
                return true;
            }
        },
        render: (renderProps)=>{
            const _connectControllerToTextField = connectControllerToTextField(renderProps), { onChange } = _connectControllerToTextField, rest = _object_without_properties(_connectControllerToTextField, [
                "onChange"
            ]);
            return /*#__PURE__*/ _jsx(Tooltip, {
                title: tooltip,
                children: /*#__PURE__*/ _jsx(Select, _object_spread_props(_object_spread({
                    collapseHelperText: false
                }, rest), {
                    label: "Billing occurrence",
                    testId: OccurrenceTextFieldTestIds.Select(),
                    disabled: disabled,
                    onChange: (e)=>{
                        const newRecurrencePeriod = e.target.value;
                        onChange(e);
                        if (newRecurrencePeriod === 'inStages') {
                            const { service } = getProps();
                            const existingOneTimeInStages = service ? serviceBillingModel.oneTimeInStages(service.billing) : null;
                            setValue('oneTimeInStagesBilling', existingOneTimeInStages || makeInitialOneTimeInStages(), {
                                shouldDirty: true
                            });
                        } else {
                            setValue('oneTimeInStagesBilling', null, {
                                shouldDirty: true
                            });
                        }
                        if (newRecurrencePeriod === 'onDemand') {
                            setValue('preapprovalPeriod', PreapprovalPeriodType.Monthly, {
                                shouldDirty: true
                            });
                        } else {
                            setValue('preapprovalPeriod', PreapprovalPeriodType.None, {
                                shouldDirty: true
                            });
                        }
                    },
                    children: getOccurrenceOptions(showOnDemand).map((param)=>{
                        let { value } = param;
                        return /*#__PURE__*/ _jsx(MenuItem, {
                            value: value,
                            testId: OccurrenceTextFieldTestIds.Option(value),
                            children: serviceBillingModel.extendedRecurrencePeriodText(value, false)
                        }, value);
                    })
                }))
            });
        }
    });
}
